import { Injectable } from '@angular/core';
import { ClientService } from '../service/client.service';
import { IotService } from '../service/iot.service';
import { BinsService } from '../service/bins.service';
import { ValidationService } from '../service/validation.service';

@Injectable({ providedIn: 'root' })
export class IotDevicesViewService {
  deviceFilter = '';
  selectedClient = '';
  filterThingGroup = '';
  filterThingSubGroup = '';
  loading = false;
  clientOptions: any[] = [];
  private initialized = false;
  private initInFlight: Promise<void> | null = null;
  private readonly storageKey = 'iot-modular:deviceFilters:v1';

  constructor(
    private iotService: IotService,
    private clientService: ClientService,
    private binService: BinsService,
    private validationService: ValidationService
  ) {}

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    if (this.initInFlight) {
      return this.initInFlight;
    }

    this.loading = true;
    this.initInFlight = (async () => {
      this.loadFilters();
      await this.clientService.getClientsForSelect();
      this.clientOptions = this.clientService.clientsArray || [];

      if (!this.iotService.devicesArray || this.iotService.devicesArray.length === 0) {
        await this.iotService.adminDeviceArray();
      }

      this.initialized = true;
    })()
      .catch((err) => {
        this.initialized = false;
        throw err;
      })
      .finally(() => {
        this.loading = false;
        this.initInFlight = null;
      });

    return this.initInFlight;
  }

  async refreshDevices(): Promise<void> {
    this.loading = true;
    this.iotService.resetDeviceArray?.();
    await this.iotService.adminDeviceArray();
    this.initialized = true;
    this.loading = false;
  }

  saveFilters(): void {
    try {
      const payload = {
        deviceFilter: this.deviceFilter || '',
        selectedClient: this.selectedClient || '',
        filterThingGroup: this.filterThingGroup || '',
        filterThingSubGroup: this.filterThingSubGroup || '',
      };
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }

  loadFilters(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      this.deviceFilter = parsed?.deviceFilter ?? this.deviceFilter;
      this.selectedClient = parsed?.selectedClient ?? this.selectedClient;
      this.filterThingGroup = parsed?.filterThingGroup ?? this.filterThingGroup;
      this.filterThingSubGroup =
        parsed?.filterThingSubGroup ?? this.filterThingSubGroup;
    } catch {
      // ignore parse/storage errors
    }
  }

  clearFilters(): void {
    this.deviceFilter = '';
    this.selectedClient = '';
    this.filterThingGroup = '';
    this.filterThingSubGroup = '';
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore storage errors
    }
  }

  filteredDevices() {
    let list = this.dedupeByThingName(this.iotService.devicesArray || []);
    if (this.deviceFilter) {
      const term = this.deviceFilter.toLowerCase();
      list = list.filter((d: any) =>
        (d.thingName || '').toLowerCase().includes(term)
      );
    }
    if (this.selectedClient) {
      list = list.filter((d: any) => d.bin?.client_id === this.selectedClient);
    }
    if (this.filterThingGroup) {
      list = list.filter((d: any) => (d.bin?.bin_group || '').includes(this.filterThingGroup));
    }
    if (this.filterThingSubGroup) {
      list = list.filter((d: any) => d.bin?.sub_group_id === this.filterThingSubGroup);
    }
    return this.dedupeByThingName(list);
  }

  private dedupeByThingName(devices: any[]): any[] {
    const seen = new Set<string>();
    const unique: any[] = [];
    for (const device of devices || []) {
      const key = device?.thingName;
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(device);
    }
    return unique;
  }
}
